/**
 * Service for document comparison functionality
 * Supports comparing two documents using AWS backend endpoints
 */
import { IPublicClientApplication } from '@azure/msal-browser';

/**
 * Returns true when `error` is a browser-level network/CORS failure.
 * See documentTranslation.service.ts for full explanation.
 */
function isCorsOrNetworkError(error: unknown): boolean {
  if (!(error instanceof TypeError)) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('load failed')
  );
}

export interface ComparisonResult {
  comparison: {
    added: string[];
    removed: string[];
    modified: string[];
  };
  statistics: {
    total_changes: number;
    additions: number;
    deletions: number;
    modifications: number;
  };
  summary: string;
}

export interface ComparisonPdfResult {
  pdf_base64: string;
  metadata: {
    file1_name: string;
    file2_name: string;
    comparison_timestamp: string;
    total_changes?: number;
  };
}

export class DocumentComparisonService {
  // TODO: Verify correct path with backend team. Trying /api/compare-pdf at root level
  private static API_BASE_URL = process.env.REACT_APP_APIM_BASE_URL || 'https://navigatordev.ai.3m.com';
  private msalInstance: IPublicClientApplication;

  constructor(msalInstance: IPublicClientApplication) {
    this.msalInstance = msalInstance;
  }

  /**
   * Get the access token for API requests
   */
  private async getAccessToken(): Promise<string> {
    const accounts = this.msalInstance.getAllAccounts();
    
    if (accounts.length === 0) {
      throw new Error('No authenticated account found');
    }

    const request = {
      scopes: [`${process.env.REACT_APP_CLIENT_ID || '262b88ff-8a8d-49fc-8485-017906bd3c29'}/user_impersonation`],
      account: accounts[0],
    };

    try {
      const response = await this.msalInstance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      const response = await this.msalInstance.acquireTokenPopup(request);
      return response.accessToken;
    }
  }

  /**
   * Compare two documents and return JSON comparison results
   * Uses /api/compare endpoint
   * 
   * NOTE: This method is available but not currently used in the UI.
   * We're using compareDocumentsPdf instead for better visual UX.
   * This JSON endpoint could be useful for:
   * - Programmatic access to comparison data
   * - Showing detailed statistics
   * - Text-based diff display
   * - Future features requiring structured comparison data
   */
  async compareDocuments(file1: File, file2: File): Promise<ComparisonResult> {
    const accessToken = await this.getAccessToken();
    const endpoint = `${DocumentComparisonService.API_BASE_URL}/compare`;

    const formData = new FormData();
    formData.append('file1', file1);
    formData.append('file2', file2);

    let response: Response;
    try {
      response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        body: formData,
      });
    } catch (fetchError) {
      if (isCorsOrNetworkError(fetchError)) {
        throw new Error(
          'Your comparison request may have been blocked by a security system. ' +
          'Please contact support if this keeps happening.'
        );
      }
      throw fetchError;
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Document comparison failed (${response.status}): ${errorText}`);
    }

    return await response.json();
  }

  /**
   * Compare two documents and return base64-encoded PDF with visual comparison
   * Uses /api/compare-pdf endpoint
   * 
   * This is the primary comparison method used in the UI because it provides
   * a visual PDF with highlighted differences that displays nicely in the modal viewer.
   */
  async compareDocumentsPdf(file1: File, file2: File): Promise<ComparisonPdfResult> {
    const accessToken = await this.getAccessToken();
    const endpoint = `${DocumentComparisonService.API_BASE_URL}/compare-pdf`;

    const formData = new FormData();
    formData.append('file1', file1);
    formData.append('file2', file2);

    let response: Response;
    try {
      response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        body: formData,
      });
    } catch (fetchError) {
      if (isCorsOrNetworkError(fetchError)) {
        throw new Error(
          'Your comparison request may have been blocked by a security system. ' +
          'Please contact support if this keeps happening.'
        );
      }
      throw fetchError;
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`PDF comparison failed (${response.status}): ${errorText}`);
    }

    return await response.json();
  }
}

// Export a singleton instance factory
export const createDocumentComparisonService = (msalInstance: IPublicClientApplication): DocumentComparisonService => {
  return new DocumentComparisonService(msalInstance);
};
